import React, { useId } from 'react';
import styled from 'styled-components';
import { Switch, Tooltip } from 'antd';
import { useMutation } from '@tanstack/react-query';

import { useGlobals } from '@shared/contexts/GlobalsContext';
import { useAppSelector } from '../../../shared/hooks/use-app-selector';
import { useAppDispatch } from '../../../shared/hooks/use-app-dispatch';
import { calendarV2OptInChanged, changeLocation } from '../../../actions';
import { canToggleNewCalendar } from '../../../selectors';
import createQueryConfig from '../../../queries';
import EVENT_BUS_EVENTS from '../../../constants/event-bus-events';
import { t } from '../../../utils/i18n';

const CALENDAR_ROUTE = '/calendar';

const StyledToggle = styled.div<{ $collapsed: boolean }>`
  /* Pinned to the bottom of the menu, and kept in view when expanded sections
     make the menu scroll. */
  position: sticky;
  bottom: 0;
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: ${({ $collapsed }) =>
    $collapsed ? 'center' : 'flex-start'};
  gap: 8px;
  padding: 16px 21px;
  background: white;

  /* Same text as the menu items; a global label style would make it bold. */
  label {
    cursor: pointer;
    margin: 0;
    font-size: ${({ theme }) => theme.antd.fontSize}px;
    font-weight: normal;
    color: ${({ theme }) => theme.antd.colorText};
  }
`;

type NewCalendarToggleProps = {
  sideMenuCollapsed: boolean;
  isOnCalendar: boolean;
};

export function NewCalendarToggle({
  sideMenuCollapsed,
  isOnCalendar,
}: NewCalendarToggleProps) {
  const dispatch = useAppDispatch();
  const { queryClient, apiClient, trackMethod, eventBus } = useGlobals();
  const canToggle = useAppSelector(canToggleNewCalendar);
  const enabled = useAppSelector(
    (state) => state.currentAccount.calendarV2OptIn
  );
  const switchId = useId();

  const { mutate, isPending } = useMutation(
    createQueryConfig(queryClient, apiClient).setNewCalendarEnabled()
  );

  if (!canToggle) return null;

  const label = t('side-menu.new-calendar-toggle.label');

  // Applied before the save, so the calendar switches at once. Reopening the
  // calendar route lets the router pick v1 or v2 for the new value.
  const applyOptIn = (value: boolean) => {
    dispatch(calendarV2OptInChanged(value));

    if (isOnCalendar) {
      dispatch(changeLocation(CALENDAR_ROUTE));
      eventBus.emit(EVENT_BUS_EVENTS.NAVIGATE, {
        payload: { route: CALENDAR_ROUTE },
      });
    }
  };

  const handleChange = (checked: boolean) => {
    applyOptIn(checked);
    trackMethod('calendar_v2_opt_in_toggled', { enabled: checked });
    mutate(checked, { onError: () => applyOptIn(!checked) });
  };

  const toggle = (
    <Switch
      size="small"
      id={switchId}
      checked={enabled}
      loading={isPending}
      disabled={isPending}
      onChange={handleChange}
      aria-label={sideMenuCollapsed ? label : undefined}
    />
  );

  return (
    <StyledToggle $collapsed={sideMenuCollapsed}>
      {sideMenuCollapsed ? (
        <Tooltip title={label} placement="right">
          {toggle}
        </Tooltip>
      ) : (
        <>
          {toggle}
          <label htmlFor={switchId}>{label}</label>
        </>
      )}
    </StyledToggle>
  );
}
